/**
 * The READ side of the offline flag-decision cache: one recovery, many readers.
 *
 * Recovering a cached decision needs the cached identity from the OS keychain,
 * which is an IPC round-trip. The allowlisted flags are read from dozens of
 * components (`useIsNewSessionListEnabled` alone has ~40 call sites), so a hook
 * that did its own keychain read would fire dozens of IPC calls on every
 * connectivity transition. Instead {@link useOfflineCachedFlagDecisionsRecovery}
 * runs ONCE, mounted beside the write side in `OpenFeatureContextSync`, and
 * publishes what it recovered through an atom that readers select from.
 */
import { offlineConnectivityStateAtom } from '@/atoms/offline-recording';
import { cachedIdentityBridge } from '@/tauri/offline-recording/identity';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import {
  atom,
  type Atom,
  getDefaultStore,
  useAtomValue,
  useSetAtom,
  useStore,
} from 'jotai';
import { selectAtom } from 'jotai/utils';
import { useCallback, useEffect } from 'react';

import {
  OFFLINE_CACHED_FLAG_KEYS,
  type OfflineCachedFlagKey,
} from './offline-cached-flag-keys';
import {
  isProviderUnreachable,
  readCachedFlagDecision,
} from './offline-recording-flag-cache';

type OfflineCachedFlagDecisions = Readonly<
  Partial<Record<OfflineCachedFlagKey, boolean>>
>;

const NO_DECISIONS: OfflineCachedFlagDecisions = Object.freeze({});

/**
 * Last online decisions recovered for the CURRENT user. Empty until something
 * is recovered, and empty again once the provider has settled online and the
 * live flag value is answering for itself.
 */
const offlineCachedFlagReplayAtom = atom<{
  generation: number;
  userRefId: string | undefined;
  decisions: OfflineCachedFlagDecisions;
}>({
  generation: 0,
  userRefId: undefined,
  decisions: NO_DECISIONS,
});

type CachedFlagReplay = readonly [userRefId: string | undefined, enabled: boolean];

const flagAtomCache = new Map<OfflineCachedFlagKey, Atom<CachedFlagReplay>>();

const cachedFlagAtom = (flagKey: OfflineCachedFlagKey): Atom<CachedFlagReplay> => {
  const existing = flagAtomCache.get(flagKey);
  if (existing) return existing;

  const derived = selectAtom(
    offlineCachedFlagReplayAtom,
    ({ userRefId, decisions }) => [userRefId, decisions[flagKey] === true] as const,
    ([previousUserRefId, previousEnabled], [nextUserRefId, nextEnabled]) =>
      previousUserRefId === nextUserRefId && previousEnabled === nextEnabled
  );
  flagAtomCache.set(flagKey, derived);
  return derived;
};

/**
 * Replay of one allowlisted flag's last online decision. `false` unless the
 * provider has been confirmed unreachable AND this user has a live cached
 * `true`, so callers OR it with the live read:
 *
 *   const fromFlagsmith = useBooleanFlagValue(KEY, false);
 *   const fromOfflineCache = useCachedFlagFallback(KEY);
 *   return fromFlagsmith || fromOfflineCache;
 *
 * Callers OR this with the live read. A fresh provider event withdraws the
 * replay, so a live `false` wins without opening a default-value gap first.
 */
export function useCachedFlagFallback(flagKey: OfflineCachedFlagKey): boolean {
  const [, enabled] = useAtomValue(cachedFlagAtom(flagKey));
  return enabled;
}

/** The same replay read outside React, from the store the hook reads. */
export function readCachedFlagFallback(flagKey: OfflineCachedFlagKey): boolean {
  const [, enabled] = getDefaultStore().get(cachedFlagAtom(flagKey));
  return enabled;
}

/**
 * Hands back the "the provider has answered for this user, online" signal.
 * Called from `OpenFeatureContextSync` at the same settle that writes the
 * cache — see {@link useOfflineCachedFlagDecisionsRecovery} for why the replay
 * is withdrawn THERE and not when connectivity returns.
 *
 * Incrementing the generation also invalidates an identity IPC that started
 * before the provider answered, preventing that delayed read from republishing
 * the replay after this withdrawal.
 */
export function useClearOfflineCachedFlagReplay(): () => void {
  const setReplay = useSetAtom(offlineCachedFlagReplayAtom);
  return useCallback(() => {
    let replayedCount = 0;
    setReplay((previous) => {
      replayedCount = OFFLINE_CACHED_FLAG_KEYS.filter(
        (flagKey) => previous.decisions[flagKey]
      ).length;
      return {
        ...previous,
        generation: previous.generation + 1,
        decisions: NO_DECISIONS,
      };
    });
    if (replayedCount > 0) {
      logger.info('[offlineFlagCache] Withdrew cached flag decisions', {
        replayedCount,
        allowlistSize: OFFLINE_CACHED_FLAG_KEYS.length,
      });
    }
  }, [setReplay]);
}

/**
 * Mount ONCE, beside the write side. Recovers every allowlisted flag's cached
 * decision for the signed-in user while the provider is unreachable.
 *
 * Connectivity returning does not withdraw the replay: the backend probe can
 * recover before Flagsmith does, and clearing in that gap swaps the selected
 * layout to its `false` default. A fresh provider event withdraws it instead.
 *
 * Desktop only: the cache is keyed on the identity in the OS keychain, which is
 * what survives a fully offline process restart. On web there is no cached
 * identity to key on, and an unreachable provider is a far shorter-lived state.
 *
 * A cold offline boot replays only once the persisted user (24-hour `getUser`)
 * supplies `user_ref_id`, one keychain IPC after first render; with no persisted
 * user nothing is replayed. See docs/desktop/offline-flag-decision-cache.md.
 */
export function useOfflineCachedFlagDecisionsRecovery(
  userRefId: string | undefined
): void {
  const connectivity = useAtomValue(offlineConnectivityStateAtom);
  const store = useStore();
  const setReplay = useSetAtom(offlineCachedFlagReplayAtom);

  // Account changes must retire the previous user's replay in this render.
  // Waiting for the effect leaves one committed frame where the old replay can
  // answer for the new user. This hook is mounted by useOfflineFlagCacheSync,
  // which already receives the current id from OpenFeatureContextSync.
  if (store.get(offlineCachedFlagReplayAtom).userRefId !== userRefId) {
    store.set(offlineCachedFlagReplayAtom, (previous) => ({
      generation: previous.generation + 1,
      userRefId,
      decisions: NO_DECISIONS,
    }));
  }

  useEffect(() => {
    if (!isTauri() || !userRefId || !isProviderUnreachable(connectivity)) return;
    let cancelled = false;
    const recoveryGeneration = store.get(offlineCachedFlagReplayAtom).generation;
    void cachedIdentityBridge
      .read()
      .then((identity) => {
        if (cancelled || identity?.userId !== userRefId) return;
        const recovered: Partial<Record<OfflineCachedFlagKey, boolean>> = {};
        for (const flagKey of OFFLINE_CACHED_FLAG_KEYS) {
          recovered[flagKey] = readCachedFlagDecision(identity?.userId, flagKey) === true;
        }
        let published = false;
        setReplay((previous) => {
          if (
            previous.generation !== recoveryGeneration ||
            previous.userRefId !== userRefId ||
            OFFLINE_CACHED_FLAG_KEYS.every(
              (flagKey) =>
                (previous.decisions[flagKey] === true) === (recovered[flagKey] === true)
            )
          ) {
            return previous;
          }
          published = true;
          return { ...previous, decisions: recovered };
        });
        if (published) {
          logger.info('[offlineFlagCache] Replayed cached flag decisions', {
            replayedCount: OFFLINE_CACHED_FLAG_KEYS.filter(
              (flagKey) => recovered[flagKey]
            ).length,
            allowlistSize: OFFLINE_CACHED_FLAG_KEYS.length,
            connectivity,
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // A cold-start failure is already fail-closed because nothing has been
        // published. A later failed re-read does not disprove the replay that
        // this user and generation already earned, so preserve it.
      });

    return () => {
      cancelled = true;
    };
  }, [connectivity, userRefId, setReplay, store]);
}
